import React, { useMemo } from 'react';
import { Modal, Table, Badge, Spinner } from 'react-bootstrap';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';

import { GET_EVENT_REGISTRATIONS } from 'helper/graphql/queries';

const EventRegistrantsModal = ({ show, onHide, event }) => {
	const { data, loading } = useQuery(GET_EVENT_REGISTRATIONS, {
		variables: { guid: event?.guid },
		skip: !show || !event?.guid,
		fetchPolicy: 'network-only',
	});

	const registrants = useMemo(() => {
		const eventNode = data?.allEvents?.edges?.[0]?.node;
		return eventNode?.registrations?.edges?.map((e) => e.node) || [];
	}, [data]);

	return (
		<Modal show={show} onHide={onHide} size="lg" centered>
			<Modal.Header closeButton className="er-modal-header">
				<div>
					<Modal.Title className="er-modal-title">
						Registered Participants
					</Modal.Title>
					{event && (
						<p className="er-event-name mb-0">{event.title}</p>
					)}
				</div>
			</Modal.Header>

			<Modal.Body className="p-0">
				{loading && (
					<div className="er-loading">
						<Spinner animation="border" size="sm" className="me-2" />
						Loading registrants…
					</div>
				)}

				{!loading && registrants.length === 0 && (
					<div className="er-empty">
						<i className="fe fe-users" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
						<p className="mt-3 mb-0 text-muted">No registrations yet.</p>
					</div>
				)}

				{!loading && registrants.length > 0 && (
					<>
						<div className="er-count-bar">
							<Badge bg="" className="er-count-badge">
								{registrants.length} participant{registrants.length !== 1 ? 's' : ''}
							</Badge>
							{event?.maxParticipants > 0 && (
								<span className="er-capacity">
									Capacity: {event.maxParticipants}
								</span>
							)}
						</div>
						<div className="er-table-wrap">
							<Table hover className="er-table mb-0">
								<thead>
									<tr>
										<th>#</th>
										<th>Name</th>
										<th>Email</th>
										<th>Country</th>
										<th>Registered At</th>
									</tr>
								</thead>
								<tbody>
									{registrants.map((r, idx) => (
										<tr key={r.guid}>
											<td className="er-idx">{idx + 1}</td>
											<td>
												<span className="er-name">
													{r.user?.firstName} {r.user?.lastName}
												</span>
											</td>
											<td className="er-email">{r.user?.email || '—'}</td>
											<td className="er-country">{r.user?.country || '—'}</td>
											<td className="er-date">
												{r.registeredAt
													? moment(r.registeredAt).format('MMM D, YYYY · h:mm A')
													: '—'}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</>
				)}
			</Modal.Body>

			<style>{`
				.er-modal-header {
					border-bottom: 2px solid #f1f5f9;
					padding: 1.25rem 1.5rem;
				}
				.er-modal-title {
					font-size: 1.0625rem;
					font-weight: 700;
					color: #1e293b;
					margin-bottom: 0.2rem;
				}
				.er-event-name {
					font-size: 0.8125rem;
					color: #94a3b8;
				}
				.er-loading, .er-empty {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					padding: 3rem 2rem;
					color: #94a3b8;
					font-size: 0.875rem;
				}
				.er-loading { flex-direction: row; }
				.er-count-bar {
					display: flex;
					align-items: center;
					gap: 0.75rem;
					padding: 0.75rem 1.5rem;
					background: #f8fafc;
					border-bottom: 1px solid #e2e8f0;
				}
				.er-count-badge {
					background: rgba(29,158,176,0.1) !important;
					color: #1d9eb0 !important;
					font-size: 0.75rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 8px;
				}
				.er-capacity {
					font-size: 0.8125rem;
					color: #94a3b8;
				}
				.er-table-wrap { overflow-x: auto; }
				.er-table thead th {
					font-size: 0.75rem;
					font-weight: 700;
					color: #94a3b8;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					background: #f8fafc;
					border-bottom: 2px solid #e2e8f0;
					padding: 0.75rem 1rem;
				}
				.er-table tbody td {
					padding: 0.75rem 1rem;
					vertical-align: middle;
					border-color: #f1f5f9;
				}
				.er-idx { color: #cbd5e1; font-size: 0.8125rem; width: 2.5rem; }
				.er-name { font-weight: 600; font-size: 0.875rem; color: #1e293b; }
				.er-email { font-size: 0.8125rem; color: #64748b; }
				.er-country { font-size: 0.8125rem; color: #64748b; }
				.er-date { font-size: 0.8125rem; color: #94a3b8; white-space: nowrap; }
			`}</style>
		</Modal>
	);
};

export default EventRegistrantsModal;
